import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { UsersThree, MagnifyingGlass, CaretRight, Plus, PencilSimple, Trash, EnvelopeSimple, Phone, IdentificationBadge, X } from "@phosphor-icons/react";

const kindLabels = { conversation: "Conversation", plan: "Plan", process: "Task", interview: "Interview", opportunity: "Opportunity", wisdom: "Lesson Learned", win: "Win" };
const kindClasses = { conversation: "k-conversation", plan: "k-plan", process: "k-process", interview: "k-interview", opportunity: "k-opportunity", wisdom: "k-wisdom", win: "k-win" };

function PersonForm({ initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || "");
  const [role, setRole] = useState(initial?.role || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [phone, setPhone] = useState(initial?.phone || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try { await onSave({ name: name.trim(), role, email, phone, notes }); }
    catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const inp = "w-full rounded-xl border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400";
  const lbl = "text-xs font-semibold uppercase tracking-[0.14em] text-slate-500";

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <form onSubmit={submit} onClick={(e)=>e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg p-6" data-testid="person-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">{initial ? "Edit person" : "New person"}</h3>
          <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X size={18}/></button>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className={lbl}>Name *</label>
            <input data-testid="person-name" required value={name} onChange={(e)=>setName(e.target.value)} className={inp} autoFocus/>
          </div>
          <div className="md:col-span-2">
            <label className={lbl}>Role / Title</label>
            <input data-testid="person-role" value={role} onChange={(e)=>setRole(e.target.value)} placeholder="e.g. Store Manager" className={inp}/>
          </div>
          <div>
            <label className={lbl}>Email</label>
            <input data-testid="person-email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} className={inp}/>
          </div>
          <div>
            <label className={lbl}>Phone</label>
            <input data-testid="person-phone" value={phone} onChange={(e)=>setPhone(e.target.value)} className={inp}/>
          </div>
          <div className="md:col-span-2">
            <label className={lbl}>Notes</label>
            <textarea data-testid="person-notes" value={notes} onChange={(e)=>setNotes(e.target.value)} rows={3} className={inp}/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onCancel} className="px-5 py-2 rounded-full text-sm border border-slate-200">Cancel</button>
          <button data-testid="person-save" type="submit" disabled={busy} className="al-gradient text-white px-5 py-2 rounded-full text-sm font-medium">{busy ? "Saving…" : (initial ? "Save changes" : "Add person")}</button>
        </div>
      </form>
    </div>
  );
}

export default function People() {
  const [people, setPeople] = useState([]);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/people"); setPeople(data); }
    catch { /* keep last known list */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = people.filter(p => p.name.toLowerCase().includes(q.toLowerCase()));

  const openTimeline = async (p) => {
    try { const { data } = await api.get(`/people/${encodeURIComponent(p.name)}`); setActive(data); }
    catch { toast.error("Couldn't load that person"); }
  };

  const savePerson = async (payload) => {
    if (editing) {
      await api.put(`/people/${editing.id}`, payload);
      toast.success("Person updated");
    } else {
      await api.post("/people", payload);
      toast.success("Person added");
    }
    setFormOpen(false); setEditing(null);
    await load();
    // Refresh active view if we were editing the currently-open person
    if (active && (editing?.id === active.id || active.name === payload.name)) openTimeline({ name: payload.name });
  };

  const deletePerson = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Their interactions in past logs will remain but they'll be removed from the people list.`)) return;
    try { await api.delete(`/people/${p.id}`); toast.success("Person deleted"); if (active?.id === p.id) setActive(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  // Defensive client-side dedupe by subentry_id + reverse-chronological order.
  // The backend also dedupes, but this keeps the UI safe if legacy data ever
  // slips through untouched.
  const timeline = useMemo(() => {
    const refs = active?.references || [];
    const seen = new Set();
    const unique = [];
    for (const r of refs) {
      const key = r.subentry_id || `nsid:${r.log_id}:${r.subject || ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(r);
    }
    // Sort by log_date desc, then created_at desc (if hydrated)
    return unique.sort((a, b) => {
      if ((b.log_date || "") !== (a.log_date || "")) return (b.log_date || "").localeCompare(a.log_date || "");
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
  }, [active]);

  return (
    <div data-testid="people-page" className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">People</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Everyone in your orbit</h1>
        </div>
        <button data-testid="people-add" onClick={() => { setEditing(null); setFormOpen(true); }} className="inline-flex items-center gap-2 al-gradient text-white px-5 py-2.5 rounded-full font-medium active:scale-95 transition-transform self-start">
          <Plus size={16} weight="bold"/> New person
        </button>
      </header>
      <div className="relative max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
        <input data-testid="people-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search…" className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-400"/>
      </div>
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-1 bg-white border border-slate-200/70 rounded-2xl p-4 max-h-[70vh] overflow-y-auto scrollbar-thin">
          {filtered.length === 0 && <div className="text-slate-400 text-sm p-4">No people yet. Click <span className="font-medium">New person</span> to add one.</div>}
          {filtered.map(p => (
            <button key={p.id} data-testid={`person-${p.name}`} onClick={()=>openTimeline(p)} className={`w-full text-left px-3 py-2.5 rounded-xl flex items-center gap-3 ${active?.name === p.name ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}>
              <div className="w-9 h-9 rounded-full al-gradient text-white flex items-center justify-center text-sm font-semibold">{p.name.slice(0,1).toUpperCase()}</div>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{p.name}</div>
                <div className={`text-xs truncate ${active?.name === p.name ? "text-slate-300" : "text-slate-500"}`}>
                  {p.role || `${p.reference_count} interactions`}
                </div>
              </div>
            </button>
          ))}
        </div>
        <div className="lg:col-span-2 bg-white border border-slate-200/70 rounded-2xl p-6 min-h-[300px]">
          {!active && <div className="text-slate-400 flex flex-col items-center justify-center py-16 gap-3"><UsersThree size={48} weight="duotone"/> Select a person to view their profile & timeline.</div>}
          {active && (
            <div>
              <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full al-gradient text-white flex items-center justify-center text-xl font-semibold">{active.name.slice(0,1).toUpperCase()}</div>
                  <div>
                    <div className="text-xl font-semibold">{active.name}</div>
                    {active.role && <div className="text-sm text-slate-500 inline-flex items-center gap-1"><IdentificationBadge size={14}/> {active.role}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid="person-edit" onClick={()=>{ setEditing(active); setFormOpen(true); }} title="Edit" className="text-slate-400 hover:text-teal-600 p-2"><PencilSimple size={16}/></button>
                  <button data-testid="person-delete" onClick={()=>deletePerson(active)} title="Delete" className="text-slate-400 hover:text-red-500 p-2"><Trash size={16}/></button>
                </div>
              </div>
              {(active.email || active.phone || active.notes) && (
                <div className="mb-6 space-y-1.5 text-sm text-slate-600">
                  {active.email && <div className="inline-flex items-center gap-2"><EnvelopeSimple size={14} className="text-slate-400"/> <a href={`mailto:${active.email}`} className="hover:underline">{active.email}</a></div>}
                  {active.phone && <div className="inline-flex items-center gap-2 ml-0 md:ml-4"><Phone size={14} className="text-slate-400"/> {active.phone}</div>}
                  {active.notes && <div className="text-sm text-slate-600 whitespace-pre-wrap mt-2">{active.notes}</div>}
                </div>
              )}
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Timeline · {timeline.length} interactions</div>
              <ol className="relative border-l border-slate-200 pl-6 space-y-4">
                {timeline.length === 0 && <li className="text-sm text-slate-400">No interactions recorded yet.</li>}
                {timeline.map((r) => (
                  <li key={`${r.log_id}-${r.subentry_id}`}>
                    <div className="absolute -left-1.5 w-3 h-3 rounded-full al-gradient" />
                    <Link to={`/log?date=${r.log_date}&sub=${r.subentry_id}`} data-testid={`person-ref-${r.subentry_id}`} className="group block rounded-lg -mx-2 px-2 py-1.5 hover:bg-slate-50 transition-colors">
                      <div className="text-xs text-slate-500">
                        {r.log_date}
                        {r.created_at && (
                          <span className="text-slate-400"> · {new Date(r.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${kindClasses[r.kind]}`}>{kindLabels[r.kind] || r.kind}</span>
                        <span className="text-sm font-medium flex-1">{r.subject || "—"}</span>
                        <CaretRight size={14} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
                      </div>
                      {r.notes && (
                        <div className="text-xs text-slate-500 mt-1 line-clamp-2 whitespace-pre-wrap">{r.notes}</div>
                      )}
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
      {formOpen && (
        <PersonForm initial={editing} onSave={savePerson} onCancel={() => { setFormOpen(false); setEditing(null); }} />
      )}
    </div>
  );
}
